import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ArrowUpDown, ChevronDown, ChevronRight, Eye, LogIn, Building2, Globe } from 'lucide-react';
import { startImpersonation } from '@/lib/impersonation';
import { DataPagination } from '@/components/DataPagination';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import type { DateRangeValue } from '@/components/DateRangeFilter';

// ── Agency team roster helpers ───────────────────────────────────────────────
// The agency Team feature (Brand-Dashboard /agency/team) models membership in two
// layers: `account_members` says who is on the AGENCY and with what account role,
// and the brand scope says which of the agency's brands they may work in.
//
// Two scoping modes, mirrored from the worker's reconcile so the labels here mean
// exactly what the dashboard enforces:
//   • all_brands = true  → every brand in the account, including ones added later
//   • all_brands = false → only the `account_member_brands` rows, each with its
//                          own per-brand role
// The account OWNER is always effectively all-brands regardless of the flag (the
// add-brand fan-out special-cases them), so it is rendered that way.
//
// `all_brands` is read as `!== false` on purpose: on a database that predates the
// agency-team migration the column is absent, and every member there WAS
// implicitly all-brands (exactly what that migration's backfill encodes).

const ACCOUNT_ROLE_ORDER: Record<string, number> = { owner: 0, co_owner: 1, admin: 2, member: 3, viewer: 4 };

/** Labels, because the raw values are snake_case and `capitalize` would render
 *  'co_owner' as "Co_owner". Mirrors the dashboard's ACCOUNT_ROLE_META labels. */
const ACCOUNT_ROLE_LABEL: Record<string, string> = {
  owner: 'Owner', co_owner: 'Co-owner', admin: 'Admin', member: 'Member', viewer: 'Viewer',
};
const roleLabel = (role?: string | null) => (role ? ACCOUNT_ROLE_LABEL[role] ?? role : '–');
/** Owner and co-owner both get the filled badge: they're the two principals. */
const isPrincipal = (role?: string | null) => role === 'owner' || role === 'co_owner';

function isAllBrands(m: any): boolean {
  return m?.role === 'owner' || m?.all_brands !== false;
}

/** brand_user_access role an all-brands member gets. Same map as the worker's
 *  brandAccessFromAccountRole: the read-only agency role is a brand viewer,
 *  everyone else is a brand admin. all_brands_role, when set, is authoritative —
 *  it is what the fan-out actually wrote. */
function allBrandsRole(m: any): string {
  return m?.all_brands_role || (m?.role === 'viewer' ? 'viewer' : 'admin');
}

/** Which brands this member (or pending invite) may work in, and as what. */
function ScopeSummary({ subject, grants, brandNameById, totalBrands }: {
  subject: any;
  grants: any[];
  brandNameById: Map<string, string>;
  totalBrands: number;
}) {
  if (isAllBrands(subject)) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Globe className="h-3 w-3 shrink-0 text-primary" />
        All brands{totalBrands ? ` (${totalBrands})` : ''} ·{' '}
        <span className="font-medium capitalize text-foreground">{allBrandsRole(subject)}</span>
      </span>
    );
  }
  if (grants.length === 0) {
    return <span className="text-xs italic text-muted-foreground">No brands assigned</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {grants.map((g: any) => (
        <span
          key={g.brand_id}
          className="inline-flex max-w-[180px] items-center gap-1 rounded border bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground"
          title={`${brandNameById.get(g.brand_id) ?? 'Brand'} — ${g.role}`}
        >
          <Building2 className="h-2.5 w-2.5 shrink-0 opacity-70" />
          <span className="truncate">{brandNameById.get(g.brand_id) ?? 'Brand'}</span>
          <span className="shrink-0 capitalize opacity-70">· {g.role}</span>
        </span>
      ))}
    </div>
  );
}

/** The agency's team roster for the row expand and the detail drawer. Joined
 *  members first, then pending invites (no profile yet — email only). */
function AgencyTeamGrid({ members, invitations, brandNameById, totalBrands, singleColumn, onImpersonate }: {
  members: any[];
  invitations: any[];
  brandNameById: Map<string, string>;
  totalBrands: number;
  /** Opens the "switch as user" confirmation for a joined member. */
  onImpersonate?: (member: any) => void;
  // The detail drawer is ~512px wide, so its grid stays one column regardless
  // of viewport (Tailwind's sm: is a viewport breakpoint, not a container one).
  singleColumn?: boolean;
}) {
  if (members.length === 0 && invitations.length === 0) {
    return <p className="text-sm italic text-muted-foreground">No team members yet</p>;
  }
  return (
    <div className={singleColumn ? 'grid gap-2' : 'grid gap-2 sm:grid-cols-2'}>
      {members.map((m: any) => {
        const name = m.profile?.full_name || m.profile?.email || m.user_id || '–';
        return (
          <div key={m.id} className="rounded-lg border bg-background/80 p-3 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {String(name).charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{name}</p>
                <p className="truncate text-xs text-muted-foreground">{m.profile?.email || '–'}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Badge
                  variant={isPrincipal(m.role) ? 'default' : 'outline'}
                  className="text-[10px]"
                >
                  {roleLabel(m.role)}
                </Badge>
                {onImpersonate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 shrink-0 p-0"
                    disabled={!m.user_id}
                    title={m.user_id
                      ? 'Switch as user (sign in as this team member)'
                      : 'No login on file for this member'}
                    onClick={() => onImpersonate(m)}
                  >
                    <LogIn className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-2 border-t pt-2">
              <ScopeSummary subject={m} grants={m.brands ?? []} brandNameById={brandNameById} totalBrands={totalBrands} />
            </div>
          </div>
        );
      })}

      {invitations.map((inv: any) => (
        <div key={inv.id} className="rounded-lg border border-dashed bg-background/50 p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground">
              {String(inv.email || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{inv.email || '–'}</p>
              <p className="truncate text-xs text-muted-foreground">
                Invited{inv.created_at ? ` ${format(new Date(inv.created_at), 'MMM d, yyyy')}` : ''}
                {inv.expires_at ? ` · expires ${format(new Date(inv.expires_at), 'MMM d')}` : ''}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Badge variant="secondary" className="text-[10px]">Pending</Badge>
              <Badge variant="outline" className="text-[10px]">{roleLabel(inv.role)}</Badge>
            </div>
          </div>
          <div className="mt-2 border-t pt-2">
            {/* An invite carries its INTENDED scope as a `brands` jsonb array,
                applied on accept — same shape as account_member_brands rows. */}
            <ScopeSummary
              subject={inv}
              grants={Array.isArray(inv.brands) ? inv.brands : []}
              brandNameById={brandNameById}
              totalBrands={totalBrands}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AgencyUsers() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<any | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [planTierFilter, setPlanTierFilter] = useState<'all' | 'free' | 'brand' | 'agency'>('all');
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const brandPortal = import.meta.env.VITE_ENV === "development" 
  ? "http://localhost:5173" 
  : process.env.VITE_ENV === "staging"
    ?"app.brandsmeetcreators.tech"
    :"app.brandsmeetcreators.com";

  // Impersonation ("switch as user") — mirrors BrandUsers. An agency has no
  // login of its own; we sign in as the agency's OWNER (accounts.owner_user_id),
  // who lands in the Brand Dashboard's agency lobby (the app resolves the agency
  // account from the DB). Same portal + same endpoint as brand impersonation.
  const [confirmImpersonateAgency, setConfirmImpersonateAgency] = useState<any | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const handleImpersonate = async () => {
    const a = confirmImpersonateAgency;
    if (!a) return;
    setIsImpersonating(true);
    try {
      const agencyName = a.name || a.owner?.email || 'agency';
      await startImpersonation({
        userId: a.owner_user_id,
        email: a.owner?.email,
        role: 'agency',
        auditTable: 'agency_impersonation',
        auditValues: { agency_name: agencyName },
      });
      toast.success(`Opening the dashboard as ${agencyName}…`);
      setConfirmImpersonateAgency(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to start impersonation');
    } finally {
      setIsImpersonating(false);
    }
  };

  // Switching into a TEAM MEMBER rather than the owner. Same endpoint, but the
  // session lands with that member's own brand_user_access rows — which is the
  // point: it is how an admin reproduces what a scoped member actually sees.
  // Pending invitees are excluded (no auth user until they accept).
  const [confirmImpersonateMember, setConfirmImpersonateMember] = useState<
    { member: any; agency: any } | null
  >(null);
  const [isImpersonatingMember, setIsImpersonatingMember] = useState(false);

  const handleImpersonateMember = async () => {
    if (!confirmImpersonateMember) return;
    const { member, agency } = confirmImpersonateMember;
    setIsImpersonatingMember(true);
    try {
      const memberName = member.profile?.full_name || member.profile?.email || 'team member';
      await startImpersonation({
        userId: member.user_id,
        email: member.profile?.email,
        role: 'agency',
        auditTable: 'agency_member_impersonation',
        auditValues: {
          agency_name: agency?.name || null,
          account_id: agency?.id || null,
          account_role: member.role || null,
          brand_scope: isAllBrands(member)
            ? 'all_brands'
            : `${(member.brands ?? []).length} brand(s)`,
        },
      });
      toast.success(`Opening the dashboard as ${memberName}…`);
      setConfirmImpersonateMember(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to start impersonation');
    } finally {
      setIsImpersonatingMember(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setCurrentPage(1); }, [planTierFilter]);

  const { data: agenciesData, isLoading } = useQuery({
    queryKey: ['agency-users', sortField, sortAsc, search, currentPage, pageSize, planTierFilter, dateRange],
    queryFn: async () => {
      const q = search.trim();

      // Search matches: agency name, owner email, OR any of the agency's
      // brand names / brand emails. Owner email lives on `profiles` and brand
      // fields live on `brand_configs` (neither on `accounts`), so resolve
      // the matching owner ids + account ids first and OR them into the
      // accounts query.
      let matchingOwnerIds: string[] = [];
      let matchingBrandAccountIds: string[] = [];
      if (q) {
        const [profilesRes, brandsRes] = await Promise.all([
          supabase.from('profiles').select('id').ilike('email', `%${q}%`),
          supabase
            .from('brand_configs')
            .select('account_id')
            .not('account_id', 'is', null)
            .or(`brand_name.ilike.%${q}%,contact_email.ilike.%${q}%,delivery_email.ilike.%${q}%`),
        ]);
        matchingOwnerIds = (profilesRes.data ?? []).map((p: any) => p.id);
        matchingBrandAccountIds = [...new Set(((brandsRes.data ?? []) as any[]).map((b: any) => b.account_id as string).filter(Boolean))];
      }

      let accountsQuery = supabase
        .from('accounts')
        .select('*', { count: 'exact' })
        .eq('type', 'agency');

      if (planTierFilter !== 'all') accountsQuery = accountsQuery.eq('plan_tier', planTierFilter);
      if (dateRange) {
        accountsQuery = accountsQuery.gte('created_at', dateRange.from.toISOString()).lte('created_at', dateRange.to.toISOString());
      }
      if (q) {
        const orParts = [`name.ilike.%${q}%`];
        if (matchingOwnerIds.length > 0) {
          orParts.push(`owner_user_id.in.(${matchingOwnerIds.join(',')})`);
        }
        if (matchingBrandAccountIds.length > 0) {
          orParts.push(`id.in.(${matchingBrandAccountIds.join(',')})`);
        }
        accountsQuery = accountsQuery.or(orParts.join(','));
      }

      const { data: accounts, count, error } = await accountsQuery
        .order(sortField, { ascending: sortAsc })
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);
      if (error) throw error;
      if (!accounts || accounts.length === 0) return { data: [], total: 0 };

      const accountIds = accounts.map((a: any) => a.id);
      const ownerIds = [...new Set(accounts.map((a: any) => a.owner_user_id).filter(Boolean))];

      // The agency team tables are newer than this page. A query against a
      // database that hasn't had the agency-team migrations applied yet 400s on
      // the unknown relation, which would take the whole Agencies tab down — so
      // the roster reads soft-fail to empty and the brands list still renders.
      const softRows = async (query: any): Promise<any[]> => {
        try {
          const { data, error } = await query;
          if (error) {
            console.warn('[AgencyUsers] agency team roster unavailable (non-fatal):', error.message);
            return [];
          }
          return data ?? [];
        } catch (err: any) {
          console.warn('[AgencyUsers] agency team roster unavailable (non-fatal):', err?.message);
          return [];
        }
      };

      const [brandsRes, memberRows, inviteRows] = await Promise.all([
        supabase.from('brand_configs').select('id, account_id, brand_name, plan_tier, is_active, contact_email, delivery_email, logo_url').in('account_id', accountIds),
        softRows(
          supabase.from('account_members').select('*').in('account_id', accountIds)
            .order('created_at', { ascending: true })
        ),
        softRows(
          supabase.from('account_invitations').select('*').in('account_id', accountIds)
            .eq('status', 'pending').order('created_at', { ascending: true })
        ),
      ]);

      // Profiles are resolved for agency owners AND team members in one read;
      // per-brand grants only matter for members scoped to specific brands, but
      // fetching them for the page's whole roster keeps it to a single query.
      const memberIds = memberRows.map((m: any) => m.id).filter(Boolean);
      const profileIds = [...new Set([
        ...ownerIds,
        ...memberRows.map((m: any) => m.user_id).filter(Boolean),
      ])];

      const [profileRows, grantRows] = await Promise.all([
        profileIds.length > 0
          ? supabase.from('profiles').select('*').in('id', profileIds).then((r: any) => r.data ?? [])
          : Promise.resolve([]),
        memberIds.length > 0
          ? softRows(supabase.from('account_member_brands').select('member_id, brand_id, role').in('member_id', memberIds))
          : Promise.resolve([]),
      ]);

      const ownerById = new Map<string, any>((profileRows ?? []).map((p: any) => [p.id, p]));
      const brandsByAccountId = new Map<string, any[]>();
      for (const bc of brandsRes.data ?? []) {
        const list = brandsByAccountId.get(bc.account_id) ?? [];
        list.push(bc);
        brandsByAccountId.set(bc.account_id, list);
      }

      const grantsByMemberId = new Map<string, any[]>();
      for (const g of grantRows ?? []) {
        const list = grantsByMemberId.get(g.member_id) ?? [];
        list.push(g);
        grantsByMemberId.set(g.member_id, list);
      }

      const membersByAccountId = new Map<string, any[]>();
      for (const m of memberRows) {
        const list = membersByAccountId.get(m.account_id) ?? [];
        list.push({ ...m, profile: ownerById.get(m.user_id) ?? null, brands: grantsByMemberId.get(m.id) ?? [] });
        membersByAccountId.set(m.account_id, list);
      }

      const invitesByAccountId = new Map<string, any[]>();
      for (const inv of inviteRows) {
        const list = invitesByAccountId.get(inv.account_id) ?? [];
        list.push(inv);
        invitesByAccountId.set(inv.account_id, list);
      }

      const data = accounts.map((a: any) => {
        const owner = ownerById.get(a.owner_user_id) ?? null;
        // Agency-owned brands have no login of their own — prefer the brand's
        // own contact info, falling back to the agency owner's email.
        const brands = (brandsByAccountId.get(a.id) ?? []).map((bc: any) => ({
          ...bc,
          email: bc.contact_email || bc.delivery_email || owner?.email || null,
        }));
        // Owner first, then admins, then members — created_at order within a tier.
        const members = (membersByAccountId.get(a.id) ?? []).slice().sort((x: any, y: any) => {
          const rank = (ACCOUNT_ROLE_ORDER[x.role] ?? 9) - (ACCOUNT_ROLE_ORDER[y.role] ?? 9);
          if (rank !== 0) return rank;
          return String(x.created_at ?? '').localeCompare(String(y.created_at ?? ''));
        });
        return { ...a, owner, brands, members, invitations: invitesByAccountId.get(a.id) ?? [] };
      });

      return { data, total: count ?? 0 };
    },
  });

  const agencies = agenciesData?.data ?? [];
  const totalAgencies = agenciesData?.total ?? 0;
  const totalPages = Math.ceil(totalAgencies / pageSize);

  // The drawer reads the roster off the selected row rather than re-querying:
  // the list query already resolves members, their profiles, their per-brand
  // grants and pending invites for every account on the page.
  const members: any[] = selectedAgency?.members ?? [];
  const invitations: any[] = selectedAgency?.invitations ?? [];
  const selectedBrandNameById = new Map<string, string>(
    ((selectedAgency?.brands ?? []) as any[]).map((bc: any) => [bc.id, bc.brand_name || 'Brand'])
  );

  const toggleSort = (field: string) => {
    setCurrentPage(1);
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  const handlePageSizeChange = (newSize: number) => {
    setCurrentPage(1);
    setPageSize(newSize);
  };

  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(field)}>
      <div className="flex items-center gap-1">{children}<ArrowUpDown className="h-3 w-3" /></div>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by agency, brand name, or email..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-9" />
        </div>
        <Select value={planTierFilter} onValueChange={(v) => setPlanTierFilter(v as typeof planTierFilter)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Plan Tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="brand">Brand</SelectItem>
            <SelectItem value="agency">Agency</SelectItem>
          </SelectContent>
        </Select>
        <DateRangeFilter
          value={dateRange}
          onChange={(r) => { setCurrentPage(1); setDateRange(r); }}
          placeholder="Select Date Range"
        />
      </div>

      <Card>
        <CardContent>
          {/* Height cap goes on the Table's own scroll wrapper — nesting a
              second overflow div puts the horizontal scrollbar below the
              clipped rows, out of reach.
              Below sm the cap is lifted: a phone draws overlay scrollbars, so
              a nested vertical scroller paints its bar on top of the rightmost
              cells. The page owns vertical scrolling there and the table keeps
              only the horizontal axis. */}
          <Table containerClassName="max-h-[600px] max-sm:max-h-none">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <SortHeader field="name">Agency Name</SortHeader>
                  <TableHead>Owner Email</TableHead>
                  <TableHead>Plan Status</TableHead>
                  <TableHead>Brands</TableHead>
                  <TableHead>Team</TableHead>
                  <SortHeader field="created_at">Created</SortHeader>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Loading...</TableCell></TableRow>
                )}
                {!isLoading && agencies.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No agencies found</TableCell></TableRow>
                )}
                {agencies.flatMap((a: any) => {
                  const isExpanded = expandedId === a.id;
                  const rows = [
                    <TableRow key={a.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedId(isExpanded ? null : a.id)}>
                      <TableCell>{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                      <TableCell className="font-medium">{a.name || <span className="text-muted-foreground italic">–</span>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.owner?.email || '–'}</TableCell>
                      <TableCell><Badge variant={a.plan_status === 'active' ? 'default' : 'secondary'} className="capitalize">{a.plan_status || '–'}</Badge></TableCell>
                      <TableCell>{a.brands.length}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {a.members.length}
                        {a.invitations.length > 0 && (
                          <span className="ml-1 text-xs text-muted-foreground">+{a.invitations.length} pending</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{a.created_at ? format(new Date(a.created_at), 'MMM d, yyyy') : '–'}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View agency details"
                            onClick={() => setSelectedAgency(a)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!a.owner?.email}
                            title={a.owner?.email ? 'Switch as user (impersonate agency owner)' : 'No owner login email on file'}
                            onClick={() => setConfirmImpersonateAgency(a)}
                          >
                            <LogIn className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>,
                  ];
                  if (isExpanded) {
                    const brandNameById = new Map<string, string>(
                      (a.brands as any[]).map((bc: any) => [bc.id, bc.brand_name || 'Brand'])
                    );
                    rows.push(
                      <TableRow key={`${a.id}-brands`} className="hover:bg-transparent">
                        <TableCell colSpan={8} className="p-0 border-b">
                          <div className="animate-in fade-in-0 slide-in-from-top-1 duration-200 bg-gradient-to-b from-muted/40 to-muted/10 px-5 py-4">
                            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                              Brands ({a.brands.length})
                            </p>
                            {a.brands.length === 0 ? (
                              <p className="text-sm text-muted-foreground italic">No brands yet</p>
                            ) : (
                              <div className="grid gap-2 sm:grid-cols-2">
                                {a.brands.map((bc: any) => (
                                  <div
                                    key={bc.id}
                                    className="flex items-center gap-3 rounded-lg border bg-background/80 p-3 shadow-sm transition-shadow hover:shadow-md"
                                  >
                                    {bc.logo_url ? (
                                      <img
                                        src={bc.logo_url}
                                        alt={bc.brand_name || 'Brand logo'}
                                        className="h-9 w-9 shrink-0 rounded-full object-cover border"
                                      />
                                    ) : (
                                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                                        {(bc.brand_name || '?').charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-medium">{bc.brand_name || '–'}</p>
                                      <p className="truncate text-xs text-muted-foreground">{bc.email || '–'}</p>
                                    </div>
                                    <div className="flex shrink-0 flex-col items-end gap-1">
                                      <Badge variant="outline" className="capitalize">{bc.plan_tier || '–'}</Badge>
                                      <Badge variant={bc.is_active ? 'default' : 'secondary'} className="text-[10px]">
                                        {bc.is_active ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Agency team: who can manage the agency, and which
                                of its brands each of them may work in. */}
                            <p className="mt-5 mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                              Team Members ({a.members.length}
                              {a.invitations.length > 0 ? ` · ${a.invitations.length} pending` : ''})
                            </p>
                            <AgencyTeamGrid
                              members={a.members}
                              invitations={a.invitations}
                              brandNameById={brandNameById}
                              totalBrands={a.brands.length}
                              onImpersonate={(m) => setConfirmImpersonateMember({ member: m, agency: a })}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return rows;
                })}
              </TableBody>
          </Table>
          {agencies.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalAgencies}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Agency Detail Drawer */}
      <Sheet open={!!selectedAgency} onOpenChange={(open) => { if (!open) setSelectedAgency(null); }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader><SheetTitle>Agency Details</SheetTitle></SheetHeader>
          {selectedAgency && (
            <div className="space-y-6 mt-6 text-sm">
              {selectedAgency.logo_url && (
                <img src={selectedAgency.logo_url} alt="Agency logo" className="h-10 object-contain rounded border" />
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Agency Name</p>
                  <p className="font-medium">{selectedAgency.name || '–'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Owner Email</p>
                  <p className="font-medium">{selectedAgency.owner?.email || '–'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Website</p>
                  {selectedAgency.website ? (
                    <a href={selectedAgency.website} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 truncate block">{selectedAgency.website}</a>
                  ) : <p className="font-medium">–</p>}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Plan Tier</p>
                  <Badge variant="outline" className="capitalize">{selectedAgency.plan_tier || '–'}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Plan Status</p>
                  <Badge variant={selectedAgency.plan_status === 'active' ? 'default' : 'secondary'} className="capitalize">{selectedAgency.plan_status || '–'}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Onboarding</p>
                  <Badge variant={selectedAgency.onboarding_completed ? 'default' : 'secondary'}>{selectedAgency.onboarding_completed ? 'Completed' : 'Pending'}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Created</p>
                  <p className="font-medium">{selectedAgency.created_at ? format(new Date(selectedAgency.created_at), 'MMM d, yyyy') : '–'}</p>
                </div>
                {selectedAgency.whop_subscription_id && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Whop Sub ID</p>
                    <p className="font-mono text-xs">{selectedAgency.whop_subscription_id}</p>
                  </div>
                )}
              </div>

              {selectedAgency.description && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Description</p>
                  <p>{selectedAgency.description}</p>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground border-b pb-1">
                  Team Members ({members.length}
                  {invitations.length > 0 ? ` · ${invitations.length} pending` : ''})
                </p>
                <AgencyTeamGrid
                  members={members}
                  invitations={invitations}
                  brandNameById={selectedBrandNameById}
                  totalBrands={selectedAgency.brands.length}
                  singleColumn
                  onImpersonate={(m) => setConfirmImpersonateMember({ member: m, agency: selectedAgency })}
                />
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground border-b pb-1">Brands ({selectedAgency.brands.length})</p>
                {selectedAgency.brands.length === 0 && <p className="text-sm text-muted-foreground">No brands yet</p>}
                {selectedAgency.brands.map((bc: any) => (
                  <div key={bc.id} className="flex items-center justify-between border rounded-md p-2">
                    <span>{bc.brand_name || '–'}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">{bc.plan_tier || '–'}</Badge>
                      <Badge variant={bc.is_active ? 'default' : 'secondary'}>{bc.is_active ? 'Active' : 'Inactive'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Switch as User (Impersonation) Confirmation */}
      <Dialog open={!!confirmImpersonateAgency} onOpenChange={(open) => { if (!open && !isImpersonating) setConfirmImpersonateAgency(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Switch as user</DialogTitle>
          </DialogHeader>
          {confirmImpersonateAgency && (
            <div className="text-sm text-muted-foreground space-y-2">
              <p>
                Log in to the agency{' '}
                <span className="font-medium text-foreground">
                  {confirmImpersonateAgency.name || confirmImpersonateAgency.owner?.email || 'this agency'}
                </span>{' '}
                as its owner{confirmImpersonateAgency.owner?.email ? ` (${confirmImpersonateAgency.owner.email})` : ''}?
              </p>
              <p>
                A one-time magic link will open <span className="font-medium text-foreground">{brandPortal}</span> in a new tab,
                signed in as the agency owner and landing on the agency dashboard. This action is logged to the audit trail.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmImpersonateAgency(null)} disabled={isImpersonating}>Cancel</Button>
            <Button onClick={handleImpersonate} disabled={isImpersonating}>
              <LogIn className="h-4 w-4 mr-2" />
              {isImpersonating ? 'Opening…' : 'Switch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Switch as a TEAM MEMBER — the session carries that member's own brand
          access, so this is how an admin sees what a scoped member sees. */}
      <Dialog
        open={!!confirmImpersonateMember}
        onOpenChange={(open) => { if (!open && !isImpersonatingMember) setConfirmImpersonateMember(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Switch as user</DialogTitle>
          </DialogHeader>
          {confirmImpersonateMember && (
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Log in as{' '}
                <span className="font-medium text-foreground">
                  {confirmImpersonateMember.member.profile?.full_name
                    || confirmImpersonateMember.member.profile?.email
                    || 'this team member'}
                </span>
                {confirmImpersonateMember.member.profile?.full_name && confirmImpersonateMember.member.profile?.email
                  ? ` (${confirmImpersonateMember.member.profile.email})`
                  : ''}
                ?
              </p>
              <p>
                Their role in{' '}
                <span className="font-medium text-foreground">
                  {confirmImpersonateMember.agency?.name || 'this agency'}
                </span>
                : <span className="font-medium text-foreground">{roleLabel(confirmImpersonateMember.member.role)}</span>.
                You will see exactly what they see —{' '}
                <span className="font-medium text-foreground">
                  {isAllBrands(confirmImpersonateMember.member)
                    ? 'every brand in the agency'
                    : `${(confirmImpersonateMember.member.brands ?? []).length} assigned brand(s)`}
                </span>
                , not the full agency.
              </p>
              <p>
                A one-time magic link will open <span className="font-medium text-foreground">{brandPortal}</span> in
                a new tab. This action is logged to the audit trail.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmImpersonateMember(null)}
              disabled={isImpersonatingMember}
            >
              Cancel
            </Button>
            <Button onClick={handleImpersonateMember} disabled={isImpersonatingMember}>
              <LogIn className="h-4 w-4 mr-2" />
              {isImpersonatingMember ? 'Opening…' : 'Switch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
